import React, { useEffect, useState } from 'react';

interface NarrationControlProps {
  markdown?: string;
  onStartNarration?: (rate: number) => void;
}

const trustedVoices = [
  'Google US English',
  'Microsoft David Desktop - English (United States)',
  'Microsoft David - English (United States)',
  'Samantha',
];

const NarrationControl: React.FC<NarrationControlProps> = ({ markdown, onStartNarration }) => {
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [selectedVoice, setSelectedVoice] = useState<SpeechSynthesisVoice | null>(null);
  const [rate, setRate] = useState<number>(1);
  const [isSpeaking, setIsSpeaking] = useState<boolean>(false);

  useEffect(() => {
    const updateVoices = () => {
      const all = window.speechSynthesis.getVoices();
      const filtered = all.filter((v) => trustedVoices.includes(v.name));
      setVoices(filtered);

      if (filtered.length > 0 && !selectedVoice) {
        setSelectedVoice(filtered[0]);
      }
    };

    window.speechSynthesis.onvoiceschanged = updateVoices;
    updateVoices();
  }, [selectedVoice]);

  const toggleSpeak = () => {
    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    } else {
      if (!selectedVoice || !markdown || markdown.trim() === '') return;

      const cleaned = markdown
        .replace(/[#>*`~_[\]]+/g, ' ')
        .replace(/\n{2,}/g, '. ')
        .replace(/\n/g, ' ')
        .trim();

      if (!cleaned) return;

      const utterance = new SpeechSynthesisUtterance(cleaned);
      utterance.voice = selectedVoice;
      utterance.rate = rate;
      utterance.pitch = 1;

      utterance.onend = () => setIsSpeaking(false);

      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utterance);
      setIsSpeaking(true);

      if (onStartNarration) onStartNarration(rate);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
      <strong style={{ color: '#ffdd80' }}>🎙️ Narration</strong>

      <select
        value={selectedVoice?.name || ''}
        onChange={(e) => {
          const voice = voices.find((v) => v.name === e.target.value);
          if (voice) setSelectedVoice(voice);
        }}
        style={{
          padding: '0.25rem 0.5rem',
          background: '#222',
          color: '#fff',
          border: '1px solid #444',
          borderRadius: '4px',
          fontSize: '0.9rem',
        }}
      >
        {voices.map((voice) => (
          <option key={voice.name} value={voice.name}>
            {voice.name}
          </option>
        ))}
      </select>

      <label style={{ display: 'flex', alignItems: 'center', color: '#ccc', fontSize: '0.9rem' }}>
        Speed:
        <input
          type="range"
          min="0.5"
          max="2"
          step="0.1"
          value={rate}
          onChange={(e) => setRate(parseFloat(e.target.value))}
          style={{ marginLeft: '0.5rem' }}
        />
        <span style={{ marginLeft: '0.5rem' }}>{rate.toFixed(1)}x</span>
      </label>

      <button
        onClick={toggleSpeak}
        style={{
          backgroundColor: isSpeaking ? '#ff6b6b' : '#333',
          color: '#fff',
          border: '1px solid #555',
          borderRadius: '4px',
          padding: '0.4rem 1rem',
          cursor: 'pointer',
          fontWeight: 'bold',
        }}
      >
        {isSpeaking ? '■ Stop' : '▶ Play'}
      </button>
    </div>
  );
};

export default NarrationControl;
